import './games.css'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Flame, Check } from 'lucide-react'
import { GAMES, gameByKey, playedToday, nextGameAfter } from './gameMeta'
import { useAuth } from '../../hooks/useAuth'
import { useFeatureFlags } from '../../lib/featureFlags'

// Basic-by-default: a game that is switched off for this person is not on their
// slate. Read through the same flags hook the gates use, so the strip and the
// Next-up door never point at a locked room.
// Bug 2026-10-30 (Jason, day 302): a fresh useFeatureFlags instance here starts as
// BASIC until ITS OWN useAuth resolves, so the curtain mounted with only Face-Off
// enabled and offered no "Next up" after Face-Off or Verdict (telemetry: every
// game_curtain from those two pages logged next:null). App.jsx's instance is the
// resolved one and publishes its set on the window; read that first.
export function useEnabledGames() {
  const { user } = useAuth()
  const flags = useFeatureFlags(user)
  const app = typeof window !== 'undefined' ? window.__nextepEnabledFeatures : null
  const on = app instanceof Set ? app : flags.enabled
  return GAMES.filter(g => on.has(g.key))
}
export function useNextGame(current, day) {
  const enabled = useEnabledGames()
  const on = new Set(enabled.map(g => g.key))
  return nextGameAfter(current, day, (k) => on.has(k))
}

// ── Today's slate — the three dailies as chips, current lit, played ticked ───
// The light connection between the games: from any one of them you can see
// what's left today and jump straight to it.
export function SlateStrip({ current, day, size = 30 }) {
  const navigate = useNavigate()
  const enabled = useEnabledGames()
  const [played, setPlayed] = useState(() => playedToday(day))
  useEffect(() => {
    setPlayed(playedToday(day))
    const on = (e) => { if (e?.detail?.day === day) setPlayed(e.detail.played || playedToday(day)) }
    window.addEventListener('nextep:games-slate', on)
    return () => window.removeEventListener('nextep:games-slate', on)
  }, [day])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} aria-label="Today’s games">
      {(!current || enabled.some(g => g.key === current) ? enabled : [gameByKey(current), ...enabled]).map(g => {
        const on = g.key === current, done = !!played[g.key]
        const Icon = g.Icon
        return (
          <button key={g.key} type="button" onClick={() => !on && navigate(g.route)} className={`gm-slate-chip${on ? ' on' : done ? ' played' : ''}`}
            style={{ '--gm-tone': g.tone, width: size, height: size }} aria-label={`${g.label}${on ? ', this game' : done ? ', played' : ', not played yet'}`} aria-current={on ? 'page' : undefined}>
            <Icon size={Math.round(size * 0.5)} strokeWidth={2.2} />
            {!on && done && <span className="tick"><Check size={8} strokeWidth={3.5} /></span>}
            {!on && !done && <span className="dot" />}
          </button>
        )
      })}
    </div>
  )
}

// ── GameFrame — the one header for the dailies ───────────────────────────────
// Back, serif title with the day eyebrow, the streak flame, and the slate strip.
// `fixed` keeps Cliffhanger's iOS-keyboard-safe fixed header (see its comment);
// everything else is sticky. The DesktopMasthead stays the desktop title.
export function GameFrame({ game, day, streak = 0, onBack, fixed = false, right = null }) {
  const g = gameByKey(game)
  return (
    <div style={{ position: fixed ? 'fixed' : 'sticky', top: 0, left: 0, right: 0, zIndex: fixed ? 10 : 'var(--z-sticky)', background: 'var(--nav-bg, var(--bg))', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: '1px solid var(--border)', paddingTop: 'max(14px, env(safe-area-inset-top))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px 10px 16px' }}>
        <button onClick={onBack} aria-label="Back" className="ui-hit" style={{ background: 'var(--surface)', border: 'none', borderRadius: 'var(--radius-full)', width: 34, height: 34, color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ArrowLeft size={18} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className={`${game}-sticky-title`} style={{ color: 'var(--text-strong)', margin: 0, fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.05 }}>{g.label}</h1>
          <div className="gm-eyebrow" style={{ color: g.tone, marginTop: 3, whiteSpace: 'nowrap' }}>{day != null ? `No. ${day}` : g.eyebrow}</div>
        </div>
        {streak > 0 && (
          <div className={`${game}-sticky-streak`} style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--amber)', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }} aria-label={`${streak}-day streak`}>
            <Flame size={14} fill="currentColor" />{streak}
          </div>
        )}
        {right}
        <SlateStrip current={game} day={day} size={26} />
      </div>
    </div>
  )
}
